import { useEffect, useRef } from "react";
import { setCheckoutTrackingSink } from "@hopper-b2b/checkout";

/**
 * Shape of the RBC publish pipeline the checkout sink forwards into. Matches
 * what `usePublishEvent` in `AppTrackingProvider` accepts: HTS event name,
 * properties to enrich, and the encrypted blobs the machine attached.
 */
export interface CheckoutTrackingPublishEvent {
  (event: {
    name: string;
    properties: Record<string, unknown>;
    encryptedProperties?: string[];
  }): void;
}

/**
 * Routes tracking fired from checkout state machines (actions and services)
 * through the same publish pipeline as React-emitted events, so they reach HTS
 * with RBC enrichment and the GA Tag Manager dataLayer / native bridge.
 *
 * The sink is registered once and reads the publisher through a ref: a new
 * `publishEvent` identity (route change, session update) must not leave a
 * cleanup-to-re-register gap where a machine event would fall back to the
 * HTS-only default. The default is restored on unmount.
 *
 * Must be rendered inside `AppTrackingContext.Provider` (the publisher reads
 * UTM trackers from it); `AppTrackingProvider` mounts it for the whole app.
 *
 * @param publishEvent RBC publish pipeline, typically `usePublishEvent()`.
 */
export const useCheckoutTrackingSink = (
  publishEvent: CheckoutTrackingPublishEvent
) => {
  const publishEventRef = useRef(publishEvent);
  publishEventRef.current = publishEvent;

  useEffect(
    () =>
      setCheckoutTrackingSink(
        ({ eventName, properties, encryptedProperties }) =>
          publishEventRef.current({
            name: eventName,
            properties: properties ?? {},
            encryptedProperties,
          })
      ),
    []
  );
};
